import React, { useState, useEffect } from 'react';
import { X, Users, CheckCircle2, MessageSquare, BarChart2, Sparkles, Download, Loader2, Settings, UserCheck, Clock, Shield, Eye, Pencil, BarChart3, List, Maximize2, Minimize2 } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import ResponseList from './Analytics/ResponseList';
import EnhancedResponseViewer from './Analytics/EnhancedResponseViewer';
import SurveyAnalytics from './Analytics/SurveyAnalytics';
import IndividualResponsesView from './Analytics/IndividualResponsesView';
import ResponseDetailModal from './Analytics/ResponseDetailModal';
import SurveyAIChatPanel from './Analytics/SurveyAIChatPanel';
import AISurveyGenerator from './AISurveyGenerator';
import useDrawerResize from '../Analytics/useDrawerResize';

const StatCard = ({ title, value, subtext, icon: Icon, color }) => (
    <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-start justify-between">
        <div>
            <p className="text-sm font-medium text-gray-500 mb-1">{title}</p>
            <h3 className="text-xl font-bold text-gray-800">{value}</h3>
            {subtext && <p className="text-xs text-gray-400 mt-1">{subtext}</p>}
        </div>
        <div className={`p-2.5 rounded-lg ${color}`}>
            <Icon size={18} className="text-white" />
        </div>
    </div>
);

const SurveyDetailModal = ({ isOpen, onClose, surveyId, onUpdate }) => {
    const [survey, setSurvey] = useState(null);
    const [instances, setInstances] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('Overview');

    // ── Resizable + fullscreen drawer ──
    const { drawerWidth, isDragging, dragHandleProps, dragHandleElement, isFullscreen, toggleFullscreen } = useDrawerResize({
        minWidth: 500, maxWidth: 1200, defaultWidth: 900,
        storageKey: 'surveyDetailDrawerWidth',
    });
    const [showEditModal, setShowEditModal] = useState(false);
    // Analytics view mode: 'aggregate' or 'individual' - persisted in localStorage
    const [analyticsViewMode, setAnalyticsViewMode] = useState(
        () => localStorage.getItem('envion_survey_analytics_view') || 'aggregate'
    );
    const [showResponseDetail, setShowResponseDetail] = useState(false);
    const [selectedInstanceId, setSelectedInstanceId] = useState(null);
    const [showAIChat, setShowAIChat] = useState(false);

    useEffect(() => {
        if (isOpen && surveyId) {
            fetchSurveyDetails();
        }
    }, [isOpen, surveyId]);

    const fetchSurveyDetails = async () => {
        try {
            setLoading(true);
            const [surveyRes, instancesRes] = await Promise.all([
                api.get(`/surveys/${surveyId}`),
                api.get(`/surveys/${surveyId}/responses`)
            ]);
            setSurvey(surveyRes.data);
            setInstances(instancesRes.data);
        } catch (error) {
            console.error("Failed to load survey details", error);
            toast.error("Failed to load survey details");
        } finally {
            setLoading(false);
        }
    };

    const handleExport = async () => {
        try {
            // Use appropriate export based on current analytics view
            const exportUrl = activeTab === 'Analytics' && analyticsViewMode === 'aggregate'
                ? `/surveys/${surveyId}/export-aggregate`
                : `/surveys/${surveyId}/export`;
            const response = await api.get(exportUrl, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            const date = new Date().toISOString().slice(0, 7);
            const viewType = analyticsViewMode === 'aggregate' ? 'Aggregate' : 'Responses';
            link.setAttribute('download', `Survey_${viewType}_${date}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success('Export downloaded!');
        } catch (error) {
            console.error("Export failed", error);
            toast.error("Export failed");
        }
    };

    const handleViewModeChange = (mode) => {
        setAnalyticsViewMode(mode);
        localStorage.setItem('envion_survey_analytics_view', mode);
    };

    const handleViewDetail = (instanceId) => {
        setSelectedInstanceId(instanceId);
        setShowResponseDetail(true);
    };

    const handleNavigateDetail = (instanceId) => {
        setSelectedInstanceId(instanceId);
    };

    const handleEditSuccess = () => {
        setShowEditModal(false);
        fetchSurveyDetails();
        onUpdate?.();
    };

    // Lock body scroll
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    // Calculate Stats
    const totalSent = instances.length;
    const completed = instances.filter(i => i.status === 'completed').length;
    const responseRate = totalSent > 0 ? Math.round((completed / totalSent) * 100) : 0;
    const sentimentScore = "Positive";

    return (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Drawer Panel — resizable + expandable */}
            <div
                className="fixed inset-y-0 right-0 z-50 bg-[#FAFAF9] shadow-2xl overflow-hidden flex flex-col animate-slide-in-right"
                style={{
                    width: isFullscreen ? '100vw' : `${drawerWidth}px`,
                    maxWidth: '100vw',
                    transition: isDragging ? 'none' : 'width 0.3s ease',
                }}
            >
                {/* ── Drag handle (left edge) — hidden in fullscreen ── */}
                {!isFullscreen && (
                    <div {...dragHandleProps}>
                        <div {...dragHandleElement} />
                    </div>
                )}

                {/* Header */}
                <div className="bg-white border-b border-[#E5E1DD] px-6 py-4 flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="p-2 bg-blue-50 rounded-lg flex-shrink-0">
                            <BarChart2 className="w-5 h-5 text-blue-600" />
                        </div>
                        {!loading && survey && (
                            <div className="min-w-0">
                                <h2 className="text-lg font-bold text-gray-900 truncate">{survey.title}</h2>
                                <p className="text-sm text-gray-500">Employee Engagement Survey</p>
                            </div>
                        )}
                        {!loading && survey && (
                            <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize flex-shrink-0 ${survey.status === 'active' ? 'bg-green-100 text-green-700' :
                                survey.status === 'draft' ? 'bg-gray-100 text-gray-700' :
                                    'bg-blue-100 text-blue-700'
                                }`}>
                                {survey.status}
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        {!loading && survey && (
                            <>
                                <button
                                    onClick={handleExport}
                                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                    title="Export CSV"
                                >
                                    <Download className="w-5 h-5" />
                                </button>
                                <button
                                    onClick={() => setShowEditModal(true)}
                                    className="px-3 py-1.5 flex items-center gap-1.5 text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                                >
                                    <Sparkles className="w-4 h-4" />
                                    <span className="text-sm font-medium">Edit</span>
                                </button>
                            </>
                        )}
                        <button
                            onClick={toggleFullscreen}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                        >
                            {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
                        </button>
                        <button
                            onClick={onClose}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Content — min-w-0 ensures content reflows instead of squishing */}
                <div className="flex-1 overflow-y-auto p-6 min-w-0">
                    {loading ? (
                        <div className="h-64 flex items-center justify-center">
                            <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
                        </div>
                    ) : !survey ? (
                        <div className="text-center py-12 text-gray-500">Survey not found</div>
                    ) : (
                        <div className="space-y-6">
                            {/* Stats Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                <StatCard
                                    title="Total Participants"
                                    value={totalSent}
                                    icon={Users}
                                    color="bg-blue-500"
                                />
                                <StatCard
                                    title="Response Rate"
                                    value={`${responseRate}%`}
                                    subtext={`${completed} / ${totalSent} completed`}
                                    icon={BarChart2}
                                    color="bg-green-500"
                                />
                                <StatCard
                                    title="Completed"
                                    value={completed}
                                    subtext="Fully completed"
                                    icon={CheckCircle2}
                                    color="bg-blue-400"
                                />
                                <StatCard
                                    title="AI Sentiment"
                                    value={sentimentScore}
                                    subtext="Based on responses"
                                    icon={MessageSquare}
                                    color="bg-blue-600"
                                />
                            </div>

                            {/* Tabs */}
                            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                                <div className="flex gap-1 border-b border-gray-200 px-4 overflow-x-auto scrollbar-hide">
                                    <button
                                        onClick={() => setActiveTab('Overview')}
                                        className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'Overview'
                                            ? 'border-blue-600 text-blue-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700'
                                            }`}
                                    >
                                        Responses
                                    </button>
                                    <button
                                        onClick={() => setActiveTab('Questions')}
                                        className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'Questions'
                                            ? 'border-blue-600 text-blue-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700'
                                            }`}
                                    >
                                        Questions ({survey.questions?.length || 0})
                                    </button>
                                    <button
                                        onClick={() => setActiveTab('Settings')}
                                        className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'Settings'
                                            ? 'border-blue-600 text-blue-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700'
                                            }`}
                                    >
                                        Settings
                                    </button>
                                    <button
                                        onClick={() => setActiveTab('Assignment')}
                                        className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'Assignment'
                                            ? 'border-blue-600 text-blue-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700'
                                            }`}
                                    >
                                        Assignment
                                    </button>
                                    <button
                                        onClick={() => setActiveTab('Analytics')}
                                        className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'Analytics'
                                            ? 'border-blue-600 text-blue-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700'
                                            }`}
                                    >
                                        Analytics
                                    </button>
                                </div>

                                <div className="p-4">
                                    {activeTab === 'Overview' && (
                                        <EnhancedResponseViewer
                                            surveyId={surveyId}
                                            instances={instances}
                                            survey={survey}
                                        />
                                    )}
                                    {activeTab === 'Questions' && (
                                        <div className="space-y-3">
                                            {survey.questions?.map((q, i) => (
                                                <div key={q.id || i} className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                                                    <div className="flex items-start justify-between">
                                                        <div className="flex-1">
                                                            <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                                                                {q.question_type}
                                                            </span>
                                                            <p className="mt-2 text-gray-800 font-medium">{q.question_text}</p>
                                                            {q.options && q.options.length > 0 && (
                                                                <div className="mt-2 flex flex-wrap gap-2">
                                                                    {q.options.map((opt, j) => (
                                                                        <span key={j} className="text-xs text-gray-500 bg-white px-2 py-1 rounded border">
                                                                            {opt}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                        {q.is_required && (
                                                            <span className="text-xs text-red-500">Required</span>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                            {(!survey.questions || survey.questions.length === 0) && (
                                                <p className="text-center text-gray-500 py-8">No questions in this survey</p>
                                            )}
                                        </div>
                                    )}
                                    {activeTab === 'Settings' && (
                                        <div className="space-y-4">
                                            {/* Trigger Type */}
                                            <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                                                        <Clock className="w-5 h-5 text-blue-600" />
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-medium text-gray-800">Trigger Type</p>
                                                        <p className="text-sm text-gray-500 capitalize">
                                                            {survey.trigger_type === 'immediate' && 'Immediate (One-off)'}
                                                            {survey.trigger_type === 'lifecycle' && `Lifecycle (${survey.trigger_config?.days_after_start || 30} days after start)`}
                                                            {survey.trigger_type === 'recurring' && `Recurring (${survey.trigger_config?.interval || 'monthly'})`}
                                                            {survey.trigger_type === 'specific_date' && 'Specific Date/Time'}
                                                            {!survey.trigger_type && 'Immediate'}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Anonymous */}
                                            <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${survey.is_anonymous ? 'bg-green-100' : 'bg-gray-100'}`}>
                                                        <Eye className={`w-5 h-5 ${survey.is_anonymous ? 'text-green-600' : 'text-gray-400'}`} />
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-medium text-gray-800">Anonymous Responses</p>
                                                        <p className="text-sm text-gray-500">
                                                            {survey.is_anonymous ? 'Employee identities are hidden' : 'Employee identities are recorded'}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Compliance */}
                                            <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${survey.is_compliance_required ? 'bg-red-100' : 'bg-gray-100'}`}>
                                                        <Shield className={`w-5 h-5 ${survey.is_compliance_required ? 'text-red-600' : 'text-gray-400'}`} />
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-medium text-gray-800">Compliance Required</p>
                                                        <p className="text-sm text-gray-500">
                                                            {survey.is_compliance_required ? 'Mandatory survey - skips consent' : 'Optional survey'}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>

                                            <p className="text-xs text-gray-400 text-center pt-2">
                                                Use "Edit with AI" to modify settings
                                            </p>
                                        </div>
                                    )}
                                    {activeTab === 'Assignment' && (
                                        <div className="space-y-4">
                                            {/* Target Audience */}
                                            <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                                                        <Users className="w-5 h-5 text-blue-600" />
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-medium text-gray-800">Target Audience</p>
                                                        <p className="text-sm text-gray-500 capitalize">
                                                            {survey.target_audience_type === 'all' && 'Company-Wide (All Employees)'}
                                                            {survey.target_audience_type === 'role' && 'Specific Roles'}
                                                            {survey.target_audience_type === 'team' && 'Specific Teams'}
                                                            {survey.target_audience_type === 'specific_employees' && 'Specific Employees'}
                                                            {!survey.target_audience_type && 'All Employees'}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Show selected roles/teams/employees */}
                                            {survey.target_audience_type === 'role' && survey.target_audience_config?.role_ids?.length > 0 && (
                                                <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                                                    <p className="text-xs font-medium text-blue-600 mb-2">Selected Roles</p>
                                                    <p className="text-sm text-blue-700">
                                                        {survey.target_audience_config.role_ids.length} role(s) selected
                                                    </p>
                                                </div>
                                            )}

                                            {survey.target_audience_type === 'team' && survey.target_audience_config?.team_ids?.length > 0 && (
                                                <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                                                    <p className="text-xs font-medium text-blue-600 mb-2">Selected Teams</p>
                                                    <p className="text-sm text-blue-700">
                                                        {survey.target_audience_config.team_ids.length} team(s) selected
                                                    </p>
                                                </div>
                                            )}

                                            {survey.target_audience_type === 'specific_employees' && survey.target_audience_config?.employee_ids?.length > 0 && (
                                                <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                                                    <p className="text-xs font-medium text-blue-600 mb-2">Selected Employees</p>
                                                    <p className="text-sm text-blue-700">
                                                        {survey.target_audience_config.employee_ids.length} employee(s) selected
                                                    </p>
                                                </div>
                                            )}

                                            {/* Deployment stats */}
                                            <div className="p-4 bg-green-50 rounded-lg border border-green-100">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                                                        <UserCheck className="w-5 h-5 text-green-600" />
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-medium text-gray-800">Deployed To</p>
                                                        <p className="text-sm text-gray-500">
                                                            {instances.length} employee(s)
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>

                                            <p className="text-xs text-gray-400 text-center pt-2">
                                                Use "Edit with AI" to modify assignment
                                            </p>
                                        </div>
                                    )}
                                    {activeTab === 'Analytics' && (
                                        <div className="space-y-4">
                                            {/* Aggregate / Individual Toggle */}
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center bg-gray-100 rounded-lg p-1">
                                                    <button
                                                        onClick={() => handleViewModeChange('aggregate')}
                                                        className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${analyticsViewMode === 'aggregate'
                                                            ? 'bg-white text-blue-700 shadow-sm'
                                                            : 'text-gray-500 hover:text-gray-700'
                                                            }`}
                                                    >
                                                        <BarChart3 size={15} />
                                                        Aggregate View
                                                    </button>
                                                    <button
                                                        onClick={() => handleViewModeChange('individual')}
                                                        className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${analyticsViewMode === 'individual'
                                                            ? 'bg-white text-blue-700 shadow-sm'
                                                            : 'text-gray-500 hover:text-gray-700'
                                                            }`}
                                                    >
                                                        <List size={15} />
                                                        Individual Responses
                                                    </button>
                                                </div>
                                                <button
                                                    onClick={handleExport}
                                                    className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors shadow-sm"
                                                >
                                                    <Download size={14} />
                                                    Export {analyticsViewMode === 'aggregate' ? 'Aggregate' : 'Individual'}
                                                </button>
                                            </div>

                                            {/* Response Rate Banner */}
                                            <div className="bg-gradient-to-r from-blue-50 to-blue-50 border border-blue-100 rounded-xl px-5 py-3 flex items-center gap-3">
                                                <Users size={18} className="text-blue-500" />
                                                <span className="text-sm font-medium text-gray-800">
                                                    {completed} of {totalSent} employees responded
                                                    <span className="text-blue-600 font-bold ml-1">({responseRate}% response rate)</span>
                                                </span>
                                            </div>

                                            {/* View Content */}
                                            {analyticsViewMode === 'aggregate' ? (
                                                <SurveyAnalytics survey={survey} instances={instances} />
                                            ) : (
                                                <IndividualResponsesView
                                                    surveyId={surveyId}
                                                    onViewDetail={handleViewDetail}
                                                    isAnonymous={survey?.is_anonymous}
                                                />
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* AI Edit Modal */}
            <AISurveyGenerator
                isOpen={showEditModal}
                onClose={() => setShowEditModal(false)}
                onSuccess={handleEditSuccess}
                editSurvey={survey}
            />

            {/* Response Detail Modal */}
            <ResponseDetailModal
                isOpen={showResponseDetail}
                onClose={() => setShowResponseDetail(false)}
                surveyId={surveyId}
                instanceId={selectedInstanceId}
                onNavigate={handleNavigateDetail}
            />

            {/* AI Chat Panel - shown when Analytics tab is active */}
            {activeTab === 'Analytics' && !loading && survey && (
                <SurveyAIChatPanel
                    surveyId={surveyId}
                    isOpen={showAIChat}
                    onToggle={() => setShowAIChat(!showAIChat)}
                    onClose={() => setShowAIChat(false)}
                />
            )}

            {/* Animation styles */}
            <style>{`
                @keyframes slide-in-right {
                    from {
                        transform: translateX(100%);
                    }
                    to {
                        transform: translateX(0);
                    }
                }
                .animate-slide-in-right {
                    animation: slide-in-right 0.3s ease-out;
                }
            `}</style>
        </>
    );
};

export default SurveyDetailModal;
